import React, { KeyboardEventHandler, useContext, useEffect, useRef } from 'react';
import { asStyledComponent } from '@cld/component-enhancer';
import { TabChildrenWrapper, TabContent, TabContentProps } from './TabContent';
import { TabsContext } from './TabsContext';

const ENTER_KEY_CODE = 13;
const SPACE_KEY_CODE = 32;

export type TabProps = Partial<Omit<TabContentProps, 'active' | 'id' | 'onClick'>> & Pick<TabContentProps, 'onClick'> & {
  ariaControls?: string;
  id: number | string;
};

export const Tab = asStyledComponent(({ ariaControls, children, disabled, icon, id, onClick, ...rest }: TabProps) => {
  const tabNodeRef = useRef(null);
  const { activeTabId, setActiveTabRef, variant, orientation, layout } = useContext(TabsContext);

  const isActive = id === activeTabId;
  const tabIndex = disabled ? -1 : 0;

  useEffect(() => {
    if (isActive) {
      setActiveTabRef?.(tabNodeRef);
    }
  }, [isActive, setActiveTabRef]);

  const handleClick = () => {
    onClick();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if ([ENTER_KEY_CODE, SPACE_KEY_CODE].includes(event.which)) {
      onClick();
    }
  };

  return (
    <TabContent
      size="sm"
      active={isActive}
      aria-controls={ariaControls}
      aria-selected={isActive}
      disabled={disabled}
      orientation={orientation}
      layout={layout}
      variant={variant}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={tabNodeRef}
      role="tab"
      tabIndex={tabIndex}
      {...rest}
    >
      {icon}
      {children && <TabChildrenWrapper>{children}</TabChildrenWrapper>}
    </TabContent>
  );
});
